import axios from 'axios';
import apiClient from './apiClient';

interface ApiResponse<T> {
  code: number | string;
  message: string;
  result: T;
}

interface ApiErrorBody {
  code?: number | string;
  message?: string;
}

export interface SaleProductItem {
  id: number;
  serialNumber: string | null;
  imei: string | null;
}

export interface SaleItemRequest {
  productVariantId: number;
  quantity: number;
  productItemIds?: number[];
}

export interface CreateSaleRequest {
  customerUserId?: number | null;
  customerName?: string;
  customerPhone?: string;
  customerAddress?: string;
  items: SaleItemRequest[];
  payment: {
    method: 'CASH';
    paidAmount: number;
  };
  note?: string;
}

export interface CreatedSale {
  id: number;
  code: string;
  status: string;
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  note?: string | null;
  createdAt: string;
}

export type SaleStatus = 'PAID' | 'CANCELED';
export type SalePaymentMethod = 'CASH';
export type SalePaymentStatus = 'PENDING' | 'COMPLETED' | 'FAILED' | 'REFUNDED';
export type SaleHistoryScope = 'all' | 'own';

export interface SaleCustomer {
  id: number | null;
  name: string | null;
  phone: string | null;
  address: string | null;
}

export interface SaleCreatedBy {
  id: number;
  username: string;
}

export interface SaleHistoryItem {
  id: number;
  productVariantId: number;
  productName: string;
  versionName: string;
  color: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  productItems: SaleProductItem[];
}

export interface SalePayment {
  id: number;
  method: SalePaymentMethod;
  amount: number;
  status: SalePaymentStatus;
  transactionCode: string | null;
}

export interface Sale {
  id: number;
  code: string;
  status: SaleStatus;
  customer: SaleCustomer | null;
  createdBy: SaleCreatedBy;
  items: SaleHistoryItem[];
  subtotal: number;
  discountAmount: number;
  totalAmount: number;
  payments: SalePayment[];
  note: string | null;
  createdAt: string;
}

export interface SalePage {
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalElements: number;
  data: Sale[];
}

export interface SaleHistoryQuery {
  page: number;
  size: number;
}

const SALE_ERROR_MESSAGES: Record<string, string> = {
  '2002': 'Sản phẩm không còn tồn tại.',
  '2003': 'Sản phẩm chưa có dữ liệu tồn kho.',
  '2014': 'Máy/IMEI đã chọn không còn tồn tại.',
  '2015': 'Khách hàng không tồn tại.',
  '3001': 'Không đủ tồn kho.',
  '3002': 'IMEI/Serial không thuộc phiên bản sản phẩm này.',
  '3003': 'Máy đã được bán hoặc không còn khả dụng.',
  '3004': 'Số lượng không khớp số máy được chọn.',
  '3005': 'Một máy không thể xuất hiện hai lần trong đơn.',
  '3006': 'Một phiên bản sản phẩm không thể xuất hiện hai dòng.',
  '3007': 'Dữ liệu tồn kho và danh sách máy đang không đồng bộ.',
  '3008': 'Tiền khách đưa chưa đủ.',
  '3009': 'Chỉ giao dịch đã thanh toán mới có thể hủy.',
  '3010': 'Bạn không có quyền truy cập giao dịch này.',
  '2013': 'Không tìm thấy giao dịch.',
};

export class SaleApiError extends Error {
  code?: number | string;
  status?: number;

  constructor(message: string, options: { code?: number | string; status?: number } = {}) {
    super(message);
    this.name = 'SaleApiError';
    this.code = options.code;
    this.status = options.status;
  }
}

export const toSaleApiError = (error: unknown): SaleApiError => {
  if (error instanceof SaleApiError) return error;

  if (axios.isAxiosError<ApiErrorBody>(error)) {
    const code = error.response?.data?.code;
    const mappedMessage = code === undefined ? undefined : SALE_ERROR_MESSAGES[String(code)];
    const fallbackMessage = error.code === 'ECONNABORTED'
      ? 'Yêu cầu đã quá thời gian chờ. Vui lòng thử lại.'
      : error.response
        ? 'Máy chủ không thể thực hiện yêu cầu bán hàng.'
        : 'Không thể kết nối đến máy chủ.';

    return new SaleApiError(mappedMessage ?? fallbackMessage, {
      code,
      status: error.response?.status,
    });
  }

  return new SaleApiError(
    error instanceof Error ? error.message : 'Đã xảy ra lỗi không xác định.',
  );
};

const withNormalizedError = async <T>(request: () => Promise<T>): Promise<T> => {
  try {
    return await request();
  } catch (error: unknown) {
    throw toSaleApiError(error);
  }
};

export const getSaleProductItems = (variantId: number): Promise<SaleProductItem[]> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<SaleProductItem[]>>(
      `/api/sales/product-variants/${variantId}/items`,
    );
    return response.data.result;
  });

export const createSale = (input: CreateSaleRequest): Promise<CreatedSale> =>
  withNormalizedError(async () => {
    const response = await apiClient.post<ApiResponse<CreatedSale>>('/api/sales', input);
    return response.data.result;
  });

export const getAllSales = (query: SaleHistoryQuery): Promise<SalePage> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<SalePage>>('/api/sales', { params: query });
    return response.data.result;
  });

export const getMySales = (query: SaleHistoryQuery): Promise<SalePage> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<SalePage>>('/api/sales/mine', { params: query });
    return response.data.result;
  });

export const getSaleById = (saleId: number): Promise<Sale> =>
  withNormalizedError(async () => {
    const response = await apiClient.get<ApiResponse<Sale>>(`/api/sales/${saleId}`);
    return response.data.result;
  });

export const cancelSale = (saleId: number): Promise<Sale> =>
  withNormalizedError(async () => {
    const response = await apiClient.post<ApiResponse<Sale>>(`/api/sales/${saleId}/cancel`);
    return response.data.result;
  });
